import ShowChartIcon from "@mui/icons-material/ShowChart";
import {
  Box,
  Button,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { formatNumber } from "../../../../infrastructure/utils/formatters";
import {
  buildDrillDownHref,
  pick,
} from "../management/dashboard/managementDashboardUtils";

/** Pipeline from initial review through construction (احداث). */
export const REQUEST_STAGE_TIMELINE_CODES = [
  "InitialReview",
  "InitiallyApproved",
  "LoanAndMehrsan",
  "SiteVisit",
  "CustomerPriceOffer",
  "CustomerApproved",
  "CustomerContract",
  "DesignAndProcurement",
  "Installation",
];

const formatPersianNumber = (value) =>
  Number(value || 0).toLocaleString("fa-IR");

const StageNode = ({ stage, index, total, maxCount }) => {
  const code = pick(stage, "stageCode", "StageCode");
  const title = pick(stage, "title", "Title") || code;
  const count = Number(pick(stage, "count", "Count") ?? 0);
  const conversion = pick(stage, "conversionRatePercent", "ConversionRatePercent");
  const isAvailable = pick(stage, "isAvailable", "IsAvailable") !== false;
  const drillDownHref = buildDrillDownHref(pick(stage, "drillDown", "DrillDown"));
  const isLast = index === total - 1;
  const intensity =
    maxCount > 0 ? Math.max(0.25, Math.min(1, count / maxCount)) : 0.25;

  const node = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: { xs: 112, sm: 128 },
        maxWidth: 148,
        flex: "0 0 auto",
        opacity: isAvailable ? 1 : 0.55,
        position: "relative",
        zIndex: 1,
      }}
    >
      <Tooltip
        title={
          isAvailable
            ? `${title}: ${formatPersianNumber(count)} پرونده`
            : "داده این مرحله در دسترس نیست"
        }
        arrow
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: isAvailable
              ? `rgba(25, 118, 210, ${0.12 + intensity * 0.55})`
              : "#f5f5f5",
            border: "3px solid",
            borderColor: isAvailable ? "#1976d2" : "#bdbdbd",
            boxShadow: isAvailable
              ? "0 2px 8px rgba(25,118,210,0.18)"
              : "none",
            mb: 1,
          }}
        >
          <Typography
            fontWeight={800}
            fontSize={count >= 1000 ? 13 : 16}
            color={isAvailable ? "#0d47a1" : "text.disabled"}
          >
            {formatPersianNumber(count)}
          </Typography>
        </Box>
      </Tooltip>

      <Typography
        fontSize={12}
        fontWeight={700}
        textAlign="center"
        sx={{ lineHeight: 1.35, minHeight: 34, px: 0.5 }}
      >
        {title}
      </Typography>

      {conversion != null && index > 0 ? (
        <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.5 }}>
          {formatNumber(conversion, 1)}٪ از قبل
        </Typography>
      ) : (
        <Typography fontSize={11} color="transparent" sx={{ mt: 0.5 }}>
          —
        </Typography>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        flex: "0 0 auto",
      }}
    >
      {drillDownHref && isAvailable ? (
        <Box
          component={RouterLink}
          to={drillDownHref}
          sx={{ textDecoration: "none", color: "inherit" }}
        >
          {node}
        </Box>
      ) : (
        node
      )}

      {!isLast ? (
        <Box
          aria-hidden
          sx={{
            width: { xs: 28, sm: 40 },
            mt: "30px",
            height: 3,
            bgcolor: "#90caf9",
            borderRadius: 1,
            flexShrink: 0,
          }}
        />
      ) : null}
    </Box>
  );
};

/**
 * Horizontal timeline of request counts from بررسی اولیه to احداث.
 * Expects ManagementDashboard GetFunnel stages payload.
 */
const RequestStageTimeline = ({
  stages = [],
  loading = false,
  error = null,
  cohortSize = 0,
  onRetry,
  stageCodes = REQUEST_STAGE_TIMELINE_CODES,
}) => {
  const timelineStages = useMemo(() => {
    const byCode = new Map(
      (stages || []).map((s) => [pick(s, "stageCode", "StageCode"), s])
    );
    return stageCodes
      .map((code) => byCode.get(code))
      .filter(Boolean);
  }, [stages, stageCodes]);

  const maxCount = useMemo(
    () =>
      timelineStages.reduce(
        (max, s) => Math.max(max, Number(pick(s, "count", "Count") ?? 0)),
        0
      ),
    [timelineStages]
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Box>
          <Typography fontWeight={700} fontSize={15}>
            تایم‌لاین پرونده‌ها (بررسی اولیه تا احداث)
          </Typography>
          <Typography fontSize={13} color="text.secondary">
            تعداد پرونده‌هایی که در بازه انتخابی ثبت شده و تا هر مرحله پیش رفته‌اند
            {cohortSize
              ? ` · کوهورت: ${formatPersianNumber(cohortSize)}`
              : ""}
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Skeleton variant="rounded" height={140} sx={{ borderRadius: "12px" }} />
      ) : error ? (
        <Box
          sx={{
            p: 2,
            borderRadius: "12px",
            bgcolor: "#fff8f6",
            border: "1px solid #ffcdd2",
          }}
        >
          <Typography color="error" fontSize={13} sx={{ mb: 1 }}>
            {error}
          </Typography>
          {onRetry ? (
            <Button size="small" variant="outlined" color="error" onClick={onRetry}>
              تلاش مجدد
            </Button>
          ) : null}
        </Box>
      ) : timelineStages.length === 0 ? (
        <Box
          sx={{
            height: 140,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#fafbfc",
            borderRadius: "12px",
            border: "1px dashed #e0e0e0",
          }}
        >
          <ShowChartIcon sx={{ fontSize: 36, color: "#bdbdbd", mb: 1 }} />
          <Typography color="text.secondary" fontSize={13}>
            داده‌ای برای تایم‌لاین مراحل وجود ندارد.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            overflowX: "auto",
            gap: 0,
            py: 1.5,
            px: 1,
            borderRadius: "12px",
            bgcolor: "#f7fbff",
            border: "1px solid #e3edf9",
          }}
        >
          {timelineStages.map((stage, index) => (
            <StageNode
              key={pick(stage, "stageCode", "StageCode") || index}
              stage={stage}
              index={index}
              total={timelineStages.length}
              maxCount={maxCount}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RequestStageTimeline;
